import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { t, tf } from '../i18n';
import { userMessage } from '../i18n/errors';
import './InlineEditableField.css';

/**
 * A field edited where it is shown (T116, FR-029, spec.md Story 7): tap the value, change it,
 * "Enregistrer". An invalid value is refused with a message and the previous value stays
 * (Acceptance 2) until the next tap; "Annuler" leaves it untouched. The editor is a text box unless `editor` draws
 * another one (a list, a month and year…) from the same draft text.
 */
export function InlineEditableField({
  label,
  display,
  initial,
  onSave,
  editor,
  inputMode,
  errorMessage = userMessage,
}: {
  label: string;
  /** What is shown when not editing. */
  display: ReactNode;
  /** The draft text the editor starts from. */
  initial: string;
  /** Saves the draft; throws (or rejects) when the value is refused. */
  onSave: (draft: string) => Promise<unknown> | void;
  editor?: (props: { id: string; value: string; onChange: (value: string) => void }) => ReactNode;
  inputMode?: 'numeric' | 'decimal' | 'text';
  /** Turns a refusal into the sentence shown. */
  errorMessage?: (error: unknown) => string;
}) {
  const id = useId();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState<string | undefined>();

  // A refusal is shown until the next tap anywhere, so it never lingers next to a valid value.
  // Cleared on `click`, once the tap has landed: clearing sooner would move the page under it.
  useEffect(() => {
    if (!error) return;
    const clear = () => setError(undefined);
    document.addEventListener('click', clear);
    return () => document.removeEventListener('click', clear);
  }, [error]);

  function open() {
    setDraft(initial);
    setError(undefined);
    setEditing(true);
  }

  function save(event: FormEvent) {
    event.preventDefault();
    setEditing(false);
    try {
      const saving = onSave(draft);
      if (saving) saving.catch((failure: unknown) => setError(errorMessage(failure)));
    } catch (refused) {
      setError(errorMessage(refused));
    }
  }

  return (
    <div className="inline-field">
      {editing ? (
        <form className="inline-field__form" onSubmit={save}>
          <label htmlFor={id}>{label}</label>
          <div className="inline-field__editor">
            {editor ? (
              editor({ id, value: draft, onChange: setDraft })
            ) : (
              <input
                id={id}
                value={draft}
                autoFocus
                {...(inputMode ? { inputMode } : {})}
                onChange={(event) => setDraft(event.target.value)}
              />
            )}
          </div>
          <div className="inline-field__actions">
            <button type="submit">{t('common.save')}</button>
            <button type="button" onClick={() => setEditing(false)}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      ) : (
        <>
          <span className="inline-field__label">{label}</span>
          <button
            type="button"
            className="inline-field__value"
            aria-label={tf('edit.field', { field: label })}
            onClick={open}
          >
            {display}
          </button>
        </>
      )}
      {error && (
        <p role="alert" className="inline-field__error">
          {error}
        </p>
      )}
    </div>
  );
}
